'use client';

import * as React from 'react';
import {
  File,
  FileArchive,
  FileAudio,
  FileCode,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
  X,
} from 'lucide-react';
import { clsx } from 'clsx';
import styles from './AttachmentControl.module.css';

export interface AttachmentControlProps extends React.HTMLAttributes<HTMLDivElement> {
  filename: string;
  onOpen?: () => void;
  onRemove?: () => void;
  tone?: 'selected' | 'neutral';
  /** Optional extension when the visible filename does not include one. */
  fileType?: string;
  /** Override the automatically inferred file-type icon. */
  icon?: React.ReactNode;
}

function FileTypeIcon({ filename, fileType }: { filename: string; fileType?: string }) {
  const extension = (fileType || filename.split('.').at(-1) || '').toLowerCase();

  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'heic'].includes(extension)) {
    return <FileImage size={16} aria-hidden="true" />;
  }
  if (['pdf', 'doc', 'docx', 'rtf', 'txt'].includes(extension)) {
    return <FileText size={16} aria-hidden="true" />;
  }
  if (['csv', 'xls', 'xlsx', 'numbers'].includes(extension)) {
    return <FileSpreadsheet size={16} aria-hidden="true" />;
  }
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) {
    return <FileArchive size={16} aria-hidden="true" />;
  }
  if (['html', 'css', 'js', 'jsx', 'ts', 'tsx', 'json', 'xml'].includes(extension)) {
    return <FileCode size={16} aria-hidden="true" />;
  }
  if (['mp3', 'wav', 'm4a', 'aac'].includes(extension)) {
    return <FileAudio size={16} aria-hidden="true" />;
  }
  if (['mp4', 'mov', 'avi', 'webm'].includes(extension)) {
    return <FileVideo size={16} aria-hidden="true" />;
  }
  return <File size={16} aria-hidden="true" />;
}

/** Compact file control used once an attachment has been added or selected. */
export const AttachmentControl = React.forwardRef<HTMLDivElement, AttachmentControlProps>(
  ({ filename, onOpen, onRemove, tone = 'selected', fileType, icon, className, ...props }, ref) => (
    <div
      ref={ref}
      className={clsx(styles.control, styles[tone], className)}
      role="group"
      aria-label={`${filename} attachment`}
      {...props}
    >
      {onOpen ? (
        <button type="button" className={styles.file} onClick={onOpen} title={filename}>
          {icon ?? <FileTypeIcon filename={filename} fileType={fileType} />}
          <span>{filename}</span>
        </button>
      ) : (
        <span className={styles.file} title={filename}>
          {icon ?? <FileTypeIcon filename={filename} fileType={fileType} />}
          <span>{filename}</span>
        </span>
      )}
      {onRemove && (
        <button
          type="button"
          className={styles.remove}
          onClick={onRemove}
          aria-label={`Remove ${filename}`}
        >
          <X size={16} aria-hidden="true" />
        </button>
      )}
    </div>
  )
);

AttachmentControl.displayName = 'AttachmentControl';
